import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Trans } from '@lingui/react';
import { i18n } from '@lingui/core';
import {
  DisplayUnits,
  MetricPressureUnit,
  ThroughputControlType,
  UnitSystem
} from 'providers/api';
import { additionalUnitLabels, BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { ProjectNumberField } from 'components/Form';
import {
  flowRateImperialToMetric,
  flowRateMetricToImperial,
  frictionLossImperialToMetric,
  frictionLossMetricToImperial,
  pumpPressureBarToKpa,
  pumpPressureImperialToMetric,
  pumpPressureKpaToBar,
  pumpPressureMetricToImperial
} from 'utils/unitConversions';
import { round } from 'utils';
import {
  OptimisationTargetType,
  OptimisationTargetLineInput
} from '../../../MineModel/types';
import PCPipe from '../../../MineModel/Pipes/PCPipe';

interface TargetLinesSectionProps {
  inputs: OptimisationTargetLineInput[];
  onChange: (inputs: OptimisationTargetLineInput[]) => void;
  pipes: Record<string, PCPipe>;
  disabled: boolean;
  metricPressureUnit?: MetricPressureUnit;
  unitSystemPreference: UnitSystem;
  throughputControlType: ThroughputControlType;
  /** Target types this user may choose. Non-P&C/admin users are limited to FlowRate. */
  allowedTargetTypes: OptimisationTargetType[];
}

const TARGET_TYPE_LABELS: Record<OptimisationTargetType, string> = {
  [OptimisationTargetType.KPaPerM]: 'Friction Loss',
  [OptimisationTargetType.PumpPressure]: 'Pump Pressure',
  [OptimisationTargetType.Yield]: 'Yield',
  [OptimisationTargetType.FlowRate]: 'Flow Rate',
};

const TargetLinesSection = ({
  inputs,
  onChange,
  pipes,
  disabled,
  metricPressureUnit,
  unitSystemPreference,
  throughputControlType,
  allowedTargetTypes,
}: TargetLinesSectionProps) => {
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal;
  const isImperial = unitSystemPreference === UnitSystem.Imperial;
  const flowRateUnitInfo = additionalUnitLabels[DisplayUnits.FlowRate];
  const flowRateAbbreviation = isImperial ? flowRateUnitInfo.imperial.abbreviation : flowRateUnitInfo.metric.abbreviation;
  const frictionLossUnitInfo = additionalUnitLabels[DisplayUnits.FrictionLoss];
  const frictionLossAbbreviation = isImperial ? frictionLossUnitInfo.imperial.abbreviation : frictionLossUnitInfo.metric.abbreviation;
  const pumpPressureUnitInfo = unitLabels[BasicDisplayUnits.PressurePump];
  const metricPumpPressureAbbreviation = isKiloPascal ? i18n._('kPa') : pumpPressureUnitInfo.metric.abbreviation;
  const pumpPressureAbbreviation = isImperial ? pumpPressureUnitInfo.imperial.abbreviation : metricPumpPressureAbbreviation;
  const showThroughputCaveat = throughputControlType !== ThroughputControlType.FlowRate;
  const toDisplayFlowRate = (metricFlowRate: number) => (isImperial ? round(flowRateMetricToImperial(metricFlowRate), 1) : metricFlowRate);

  // Canonical storage: kPa/m for friction loss, bar for pump pressure. Imperial is display only.
  const toDisplayFrictionLoss = (kPaPerM: number) => (isImperial ? round(frictionLossMetricToImperial(kPaPerM), 3) : kPaPerM);
  const fromDisplayFrictionLoss = (value: number) => (isImperial ? frictionLossImperialToMetric(value) : value);
  const toDisplayPumpPressure = (bar: number) => {
    if (isImperial) return round(pumpPressureMetricToImperial(bar), 1);
    return isKiloPascal ? pumpPressureBarToKpa(bar) : bar;
  };
  const fromDisplayPumpPressure = (value: number) => {
    if (isImperial) return pumpPressureImperialToMetric(value);
    return isKiloPascal ? pumpPressureKpaToBar(value) : value;
  };
  const pipeOptions = React.useMemo(() => Object.values(pipes).map((pipe) => ({
    id: pipe.pipeId,
    label: pipe.name || `Pipe ${pipe.pipeId}`,
  })), [pipes]);

  const handleAdd = () => {
    onChange([...inputs, { targetType: allowedTargetTypes[0] ?? OptimisationTargetType.KPaPerM }]);
  };

  const handleRemove = (index: number) => {
    onChange(inputs.filter((_, i) => i !== index));
  };

  const handleUpdate = (index: number, updates: Partial<OptimisationTargetLineInput>) => {
    onChange(inputs.map((input, i) => (i === index ? { ...input, ...updates } : input)));
  };

  return (
    <Accordion defaultExpanded={false} disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 'unset' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          <Trans id="Target Lines">Target Lines</Trans>
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        {!disabled && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button size="small" color="secondary" startIcon={<AddIcon />} onClick={handleAdd}>
              <Trans id="Add Target">Add Target</Trans>
            </Button>
          </Box>
        )}

        {inputs.map((input, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={index} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="secondary">
                {`Target ${index + 1}`}
              </Typography>
              {!disabled && (
                <IconButton size="small" onClick={() => handleRemove(index)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ width: '48%' }}>
                <TextField
                  select
                  disabled={disabled}
                  size="small"
                  fullWidth
                  color="secondary"
                  value={input.targetType}
                  onChange={(e) => handleUpdate(index, {
                    targetType: parseInt(e.target.value, 10) as OptimisationTargetType,
                    kPaPerMTarget: undefined,
                    targetPipeId: undefined,
                    pumpPressureTarget: undefined,
                    yieldTarget: undefined,
                    flowRateTarget: undefined,
                  })}
                  label={i18n._('Target Type')}
                >
                  {allowedTargetTypes.map((targetType) => (
                    <MenuItem key={targetType} value={targetType}>{TARGET_TYPE_LABELS[targetType]}</MenuItem>
                  ))}
                </TextField>
              </Box>

              {input.targetType === OptimisationTargetType.KPaPerM && (
                <>
                  <Box sx={{ width: '48%' }}>
                    <ProjectNumberField
                      color="secondary"
                      disabled={disabled}
                      size="small"
                      fullWidth
                      decimalPlaces={isImperial ? 3 : 1}
                      value={input.kPaPerMTarget === undefined ? undefined : toDisplayFrictionLoss(input.kPaPerMTarget)}
                      onChange={(parsed) => handleUpdate(index, {
                        kPaPerMTarget: parsed === undefined ? undefined : fromDisplayFrictionLoss(parsed),
                      })}
                      label={i18n._('Friction Loss Target')}
                      slotProps={{
                        input: { endAdornment: <InputAdornment position="end">{frictionLossAbbreviation}</InputAdornment> },
                      }}
                    />
                  </Box>
                  <Box sx={{ width: '48%' }}>
                    <TextField
                      select
                      color="secondary"
                      disabled={disabled}
                      size="small"
                      fullWidth
                      value={input.targetPipeId ?? ''}
                      onChange={(e) => handleUpdate(index, { targetPipeId: e.target.value })}
                      label={i18n._('Pipe')}
                    >
                      {pipeOptions.map((pipe) => (
                        <MenuItem key={pipe.id} value={pipe.id}>{pipe.label}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </>
              )}

              {input.targetType === OptimisationTargetType.PumpPressure && (
                <Box sx={{ width: '48%' }}>
                  <ProjectNumberField
                    color="secondary"
                    disabled={disabled}
                    size="small"
                    fullWidth
                    decimalPlaces={!isImperial && isKiloPascal ? 0 : 1}
                    value={input.pumpPressureTarget === undefined ? undefined : toDisplayPumpPressure(input.pumpPressureTarget)}
                    onChange={(parsed) => handleUpdate(index, {
                      pumpPressureTarget: parsed === undefined ? undefined : fromDisplayPumpPressure(parsed),
                    })}
                    label={i18n._('Pump Pressure Target')}
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">{pumpPressureAbbreviation}</InputAdornment> },
                    }}
                  />
                </Box>
              )}

              {input.targetType === OptimisationTargetType.Yield && (
                <Box sx={{ width: '48%' }}>
                  <TextField
                    color="secondary"
                    disabled={disabled}
                    size="small"
                    type="number"
                    fullWidth
                    value={input.yieldTarget ?? ''}
                    onChange={(e) => handleUpdate(index, { yieldTarget: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                    label={i18n._('Yield Target')}
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">Pa</InputAdornment> },
                    }}
                  />
                </Box>
              )}

              {input.targetType === OptimisationTargetType.FlowRate && (
                <>
                  <Box sx={{ width: '48%' }}>
                    <ProjectNumberField
                      color="secondary"
                      disabled={disabled}
                      size="small"
                      fullWidth
                      decimalPlaces={1}
                      value={input.flowRateTarget === undefined ? undefined : toDisplayFlowRate(input.flowRateTarget)}
                      onChange={(parsed) => handleUpdate(index, {
                        flowRateTarget: parsed === undefined || !isImperial ? parsed : flowRateImperialToMetric(parsed),
                      })}
                      label={i18n._('Flow Rate Target')}
                      slotProps={{
                        input: { endAdornment: <InputAdornment position="end">{flowRateAbbreviation}</InputAdornment> },
                      }}
                    />
                  </Box>
                  {showThroughputCaveat && (
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="caption" color="text.secondary">
                        <Trans id="Optimised at this flow rate, tonnage will differ from the recipe's">
                          Optimised at this flow rate, tonnage will differ from the recipe&apos;s
                        </Trans>
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        ))}

        {inputs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            <Trans id="No target lines added">No target lines added. Click &quot;Add Target&quot; to add one.</Trans>
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default TargetLinesSection;
